import { useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';

import { Cluster, Gpu, ModalProps } from '@typings';
import { SUPPORTED_GPU_MAKES } from '@constants';
import {
  createClusterResourcePreset,
  updateClusterResourcePreset,
} from '@services';
import {
  as,
  getFormattedMemory,
  getGpuMakeKeys,
  getParsedMemory,
  invariant,
  isObject,
  noop,
  normalizeFormErrors,
  normalizeResourceGpuSpec,
  toastifyResponseError,
  transformNumberFormat,
} from '@utils';
import { isGpuSpec } from '@utils/guards';

import { Button, Field, Modal, Render } from '@components';
import { ClusterResourcePresetChips } from '@components/Ui';

import { ClusterSettingsResourceGpuModelsField } from '../ClusterSettingsResourceGpuModelsField';

type Schema = z.infer<ReturnType<typeof makeSchema>>;

type SchemaParams = {
  nodePools: Cluster.ResourcePoolType[];
};

const makeSchema = ({ nodePools }: SchemaParams) => {
  const MIN_CPU = 0.01;
  const maxCpu = Math.max(...nodePools.map(({ cpu }) => cpu));
  const MIN_MEMORY = 64 * 10 ** 6;
  const maxMemory = Math.max(...nodePools.map(({ memory }) => memory));
  const getMaxGpu = ({ gpuMake, gpuModelName }: Gpu.ProfileModel) => {
    const { gpuModelKey } = getGpuMakeKeys(gpuMake);
    const isSharedGpuModel = !gpuModelName;

    const gpuModels = nodePools.filter((nodePool) => {
      const nodePoolSpec = normalizeResourceGpuSpec(nodePool);
      const gpuCount = nodePoolSpec[gpuModelKey]?.count;

      return isSharedGpuModel
        ? gpuCount
        : gpuCount && gpuModelName === nodePoolSpec[gpuModelKey]?.model;
    });

    const sortedGpuModels = (
      gpuModels as Array<Cluster.ResourcePoolType & Required<Gpu.Record>>
    ).sort((a, b) => b[gpuModelKey].count - a[gpuModelKey].count);

    return sortedGpuModels[0]?.[gpuModelKey]?.count ?? 0;
  };

  return z
    .object({
      name: z.string().min(1),
      cpu: z.coerce.number().superRefine((cpu: number, context) => {
        if (cpu < MIN_CPU) {
          context.addIssue({
            code: z.ZodIssueCode.too_small,
            type: 'number',
            inclusive: false,
            minimum: MIN_CPU,
            message: `Number of CPU cores cannot be less than ${MIN_CPU} cores`,
          });

          return z.NEVER;
        }

        if (cpu > maxCpu) {
          context.addIssue({
            code: z.ZodIssueCode.too_big,
            type: 'number',
            inclusive: false,
            maximum: maxCpu,
            message:
              'Number of CPU cores cannot be larger than number of cores in any node pool',
          });
        }
      }),
      gpuModels: z
        .object({
          gpuMake: z.enum(SUPPORTED_GPU_MAKES),
          gpuModelName: z.string().optional(),
        })
        .array()
        .optional(),
      creditsPerHour: z.coerce.number().nonnegative(),
      memory: z.coerce.number(),
      memoryPrefix: z.string(),
      isPreemptible: z.boolean(),
      isSchedulerEnabled: z.boolean().optional(),
      nvidiaGpu: z.coerce.number().optional(),
      intelGpu: z.coerce.number().optional(),
      amdGpu: z.coerce.number().optional(),
      resourcePoolNames: z
        .object({ name: z.string(), value: z.boolean() })
        .array(),
    })
    .superRefine(({ memory, memoryPrefix }, context) => {
      const parsedMemory = getParsedMemory(`${memory}${memoryPrefix}`) ?? 0;
      const isMemoryNaN = Number.isNaN(memory);

      if (isMemoryNaN) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['memory'],
          message: 'Memory is not a number',
        });

        return z.NEVER;
      }

      if (!parsedMemory) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['memory'],
          message: 'Memory cannot be blank',
        });

        return z.NEVER;
      }

      if (parsedMemory < MIN_MEMORY) {
        context.addIssue({
          code: z.ZodIssueCode.too_small,
          type: 'number',
          inclusive: false,
          minimum: MIN_MEMORY,
          path: ['memory'],
          message: `Memory cannot be less than ${getFormattedMemory(
            MIN_MEMORY,
          )}`,
        });

        return z.NEVER;
      }

      if (parsedMemory > maxMemory) {
        context.addIssue({
          code: z.ZodIssueCode.too_big,
          type: 'number',
          inclusive: false,
          maximum: maxMemory,
          path: ['memory'],
          message:
            'Memory cannot be larger than amount of memory in any node pool',
        });

        return z.NEVER;
      }
    })
    .superRefine(({ gpuModels = [], ...data }, context) => {
      gpuModels.forEach((gpuModel) => {
        const { gpuModelKey } = getGpuMakeKeys(gpuModel.gpuMake);
        const gpu = normalizeResourceGpuSpec(data as Gpu.Model);
        const gpuQuantity = gpu[gpuModelKey]?.count;
        const maxGpuQuantity = getMaxGpu(gpuModel);

        if (!gpuQuantity) {
          context.addIssue({
            code: z.ZodIssueCode.custom,
            path: [gpuModelKey],
            message: 'Number cannot be 0 for GPU model',
          });

          return z.NEVER;
        }

        if (gpuQuantity > maxGpuQuantity) {
          context.addIssue({
            code: z.ZodIssueCode.custom,
            path: [gpuModelKey],
            message:
              'Number cannot be larger than number of GPUs for selected GPU model',
          });
        }
      });

      return z.NEVER;
    })
    .refine(
      ({ cpu, memory, isPreemptible }) =>
        nodePools.some((nodePool) => {
          return (
            cpu <= nodePool.availableCpu &&
            memory <= nodePool.availableMemory &&
            isPreemptible === (nodePool.isPreemptible ?? false)
          );
        }),
      {
        message: "This preset won't fit into any node pool",
        path: ['name'],
      },
    );
};

/**
 * todo: fix type
 */
type ResourcePreset = any;

type Props = ModalProps & {
  clusterName: string;
  nodePools: Cluster.ResourcePoolType[];
  edit?: boolean;
  name?: string;
  resourcePreset?: Cluster.ResourcePreset;
  onAddResourcePreset?: (resourcePreset: Cluster.ResourcePreset) => void;
  onResourcePresetUpdate?: (
    resourcePreset: Partial<Cluster.ResourcePreset>,
  ) => void;
};

export const ClusterResourcePresetModal = ({
  edit = false,
  name,
  nodePools,
  clusterName,
  resourcePreset,
  closeModal = noop,
  onAddResourcePreset,
  onResourcePresetUpdate,
}: Props) => {
  const getDefaultValues = (): ResourcePreset | undefined => {
    const handleGpu = (gpu: number | Gpu.Spec | undefined) =>
      isGpuSpec(gpu) ? gpu.count : gpu;

    const {
      isPreemptible,
      schedulerEnabled,
      name,
      cpu,
      memory,
      creditsPerHour,
      resourcePoolNames = [],
      ...restResourcePreset
    } = as.o<Cluster.ResourcePreset>(resourcePreset);

    const defaultResourcePoolNames = nodePools.map(({ name }) => ({
      name,
      value: resourcePoolNames.includes(name),
    }));

    if (!resourcePreset) {
      return { resourcePoolNames: defaultResourcePoolNames, gpuModels: [] };
    }

    const formattedMemory = getFormattedMemory(memory, {
      separator: ' ',
      fix: 0,
    });
    const { amdGpu, intelGpu, nvidiaGpu } = restResourcePreset;
    const [memoryValue, memoryPrefix] = formattedMemory.split(' ');
    const gpuModels = SUPPORTED_GPU_MAKES.reduce<Gpu.ProfileModel[]>(
      (models, gpuMake) => {
        const { gpuModelKey, gpuModelNameKey } = getGpuMakeKeys(gpuMake);

        const gpuModel = restResourcePreset[gpuModelKey];
        const isSpec = isGpuSpec(gpuModel);
        const gpuQuantity = isSpec ? gpuModel.count : gpuModel;
        const gpuModelName = isSpec
          ? gpuModel.model
          : // @ts-expect-error todo: fix type
            restResourcePreset[gpuModelNameKey];

        return gpuQuantity ? [...models, { gpuMake, gpuModelName }] : models;
      },
      [],
    );

    return {
      name,
      memory: memoryValue,
      memoryPrefix,
      amdGpu: handleGpu(amdGpu),
      intelGpu: handleGpu(intelGpu),
      nvidiaGpu: handleGpu(nvidiaGpu),
      gpuModels,
      cpu,
      isSchedulerEnabled: schedulerEnabled,
      isPreemptible,
      creditsPerHour,
      resourcePoolNames: defaultResourcePoolNames,
    };
  };

  const methods = useForm<Schema>({
    resolver: zodResolver(makeSchema({ nodePools })),
    defaultValues: getDefaultValues(),
  });

  const [loading, setLoading] = useState(false);

  const { register, formState, handleSubmit } = methods;
  const errors = normalizeFormErrors<keyof Schema>(formState.errors);

  const normalizeGpu = (
    gpu: Gpu.Model,
    gpus: Gpu.Profile[] = [],
  ): Gpu.Record => {
    const resource = normalizeResourceGpuSpec(gpu);

    return gpus.reduce<Gpu.Record>((models, { gpuMake, gpuModelName }) => {
      const { gpuModelKey } = getGpuMakeKeys(gpuMake);
      const isGpu = isObject(resource[gpuModelKey]);

      if (!gpuMake || !isGpu) {
        return models;
      }

      return {
        ...models,
        [gpuModelKey]: {
          ...(resource[gpuModelKey] as Gpu.Record),
          model: gpuModelName,
        },
      };
    }, {});
  };

  const handleFormSubmit = handleSubmit(
    async ({
      name: presetName,
      cpu,
      memory,
      memoryPrefix,
      isSchedulerEnabled,
      isPreemptible,
      creditsPerHour,
      resourcePoolNames,
      gpuModels,
      nvidiaGpu,
      amdGpu,
      intelGpu,
    }) => {
      try {
        setLoading(true);

        const parsedMemory = getParsedMemory(`${memory}${memoryPrefix}`) ?? 0;
        const gpu = normalizeGpu(
          {
            nvidiaGpu,
            intelGpu,
            amdGpu,
          },
          gpuModels,
        );

        const formattedResourcePoolNames = resourcePoolNames
          .filter(({ value }) => value)
          .map(({ name }) => name);

        invariant(clusterName);

        const payload = {
          ...gpu,
          clusterName,
          name: presetName,
          memory: parsedMemory,
          cpu,
          isPreemptible,
          isSchedulerEnabled: isSchedulerEnabled ?? false,
          creditsPerHour,
          resourcePoolNames: formattedResourcePoolNames,
        };

        if (edit) {
          await updateClusterResourcePreset(name as string, payload);
          onResourcePresetUpdate?.(payload);
        } else {
          const clusterResponse = await createClusterResourcePreset(payload);

          const newResourcePreset =
            clusterResponse.orchestrator.resourcePresets?.find(
              ({ name }) => name === presetName,
            );

          if (newResourcePreset) {
            onAddResourcePreset?.(newResourcePreset);
          }
        }

        closeModal();
      } catch (error) {
        toastifyResponseError(error);
      } finally {
        setLoading(false);
      }
    },
  );

  return (
    <Modal.Content className="w-[578px]">
      <Modal.Header sticky className="capitalize">
        {edit ? `Edit preset ${name}` : 'Create resouce preset'}
      </Modal.Header>
      <FormProvider {...methods}>
        <form
          noValidate
          className="flex flex-col gap-6"
          onSubmit={handleFormSubmit}
        >
          <Field.Input
            {...register('name')}
            required
            label="Name"
            note="Name should uniqly identify resource preset within cluster"
            className="w-full"
            error={errors.name}
          />
          <Field.Input
            {...register('cpu', { setValueAs: transformNumberFormat })}
            required
            type="number"
            label="Number of vCPU cores"
            note="Amount of virtual CPU cores to allocate"
            className="w-full"
            error={errors.cpu}
          />
          <div className="flex gap-3">
            <Field.Input
              {...register('memory', { setValueAs: transformNumberFormat })}
              required
              label="Amount of RAM"
              note="Amount of RAM to allocate"
              className="w-full"
              containerClassName="flex-1"
              error={errors.memory}
            />
            <Field.Select
              {...register('memoryPrefix')}
              containerClassName="basis-[140px]"
            >
              <option value="MB">MB</option>
              <option value="GB">GB</option>
              <option value="TB">TB</option>
            </Field.Select>
          </div>
          <ClusterSettingsResourceGpuModelsField />
          <Render if={nodePools}>
            <div>
              <Field.Label className="capitalize text-neural-04">
                Resource pool names
              </Field.Label>
              <ClusterResourcePresetChips />
            </div>
          </Render>
          <Field.Checkbox
            {...register('isSchedulerEnabled')}
            note="Enables suspending and rescheduling of jobs by the platform"
          >
            Round-robin
          </Field.Checkbox>
          <Field.Checkbox
            {...register('isPreemptible')}
            note="Marks that this preset requires preemtible node"
          >
            Preemptability
          </Field.Checkbox>
          <Field.Input
            {...register('creditsPerHour')}
            type="number"
            label="Price for hour"
            note="The cost of using this preset for 1 hour in credits"
            className="w-full"
            error={errors.creditsPerHour}
          />
        </form>
      </FormProvider>
      <Modal.Footer sticky className="items-center justify-between bg-white">
        <p className="text-neural-03">
          <span className="text-error">*</span> Required
        </p>
        <Button className="px-10" loading={loading} onClick={handleFormSubmit}>
          Save
        </Button>
      </Modal.Footer>
    </Modal.Content>
  );
};
